<template>
  <v-card
    class="announcement-card pa-6"
    rounded="xl"
    variant="flat"
    :class="variantStyles.gradientClass"
  >
    <div class="d-flex flex-column flex-md-row ga-4 align-start">
      <div class="flex-grow-1">
        <div class="d-flex align-center mb-3">
          <v-avatar size="42" class="mr-3" color="white">
            <v-icon :color="iconColor" :icon="variantStyles.icon" />
          </v-avatar>
          <div>
            <div class="text-subtitle-1 font-weight-bold">
              {{ announcement.title }}
            </div>
            <div class="text-caption text-white text-uppercase opacity-80">
              {{ variantLabel }}
            </div>
          </div>
        </div>
        <p class="text-body-2 announcement-card__body mb-0">
          {{ announcement.body }}
        </p>

        <div
          v-if="announcementVideoCatalogId"
          class="announcement-card__video mt-4"
        >
          <div class="announcement-card__storage-video-frame">
            <MuxVideoPlayer
              :video-id="announcementVideoCatalogId"
              class="announcement-card__storage-video"
            />
          </div>
        </div>

        <div
          v-else-if="videoEmbedUrl"
          class="announcement-card__video mt-4"
        >
          <div class="announcement-card__youtube-frame">
            <iframe
              :src="videoEmbedUrl"
              title="Video of the week"
              frameborder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowfullscreen
              class="announcement-card__youtube-iframe"
            ></iframe>
          </div>
        </div>
      </div>

      <div v-if="hasCta" class="d-flex align-md-center">
        <v-btn
          size="large"
          rounded="lg"
          variant="flat"
          :color="variantStyles.buttonColor"
          :href="announcement.ctaUrl"
          target="_blank"
          rel="noopener"
        >
          {{ ctaLabel }}
        </v-btn>
      </div>
    </div>
  </v-card>
</template>

<script setup>
import { computed } from "vue";
import MuxVideoPlayer from "@/components/MuxVideoPlayer.vue";
import { ANNOUNCEMENT_VARIANT_MAP } from "@/constants/announcements";

const YOUTUBE_REGEX =
  /^(https?:\/\/)?(www\.)?(youtube\.com\/watch\?v=|youtu\.be\/)([\w-]{11})(.*)$/i;

const props = defineProps({
  announcement: {
    type: Object,
    required: true,
  },
});

const variantStyles = computed(() => {
  const variant = props.announcement?.variant || "default";
  return (
    ANNOUNCEMENT_VARIANT_MAP[variant] ||
    ANNOUNCEMENT_VARIANT_MAP.default || {
      gradientClass: "announcement-card--default",
      buttonColor: "primary",
      icon: "mdi-bullhorn",
      fallbackCtaLabel: "Learn more",
      label: "Announcement",
    }
  );
});

const variantLabel = computed(() => variantStyles.value.label || "Announcement");
const iconColor = computed(() => {
  const variant = props.announcement?.variant || "default";
  if (variant === "youtube") return "red-darken-1";
  if (variant === "whatsapp") return "success";
  if (variant === "default") return "primary";
  return variantStyles.value.buttonColor || "primary";
});

const hasCta = computed(() => Boolean(props.announcement?.ctaUrl));

const ctaLabel = computed(
  () =>
    props.announcement?.ctaLabel?.trim() ||
    variantStyles.value.fallbackCtaLabel ||
    "Learn more",
);

const announcementVideoCatalogId = computed(
  () => props.announcement?.videoCatalogId?.trim() || "",
);

const videoEmbedUrl = computed(() => {
  const url = props.announcement?.youtubeUrl?.trim();
  if (!url || !YOUTUBE_REGEX.test(url)) return "";

  // Extract video id and build embed URL
  const match = url.match(YOUTUBE_REGEX);
  const videoId = match?.[4];
  if (!videoId) return "";
  return `https://www.youtube.com/embed/${videoId}`;
});
</script>

<style scoped>
.announcement-card {
  color: #fff;
  background: linear-gradient(120deg, #41487a, #6571d4);
  box-shadow: none !important;
}

.announcement-card__body {
  white-space: pre-line;
}

.announcement-card--default {
  background: rgb(var(--v-theme-primary));
}

.announcement-card--youtube {
  background: linear-gradient(120deg, #c31432, #89216b);
}

.announcement-card--whatsapp {
  background: linear-gradient(120deg, #0bab64, #3bb78f);
}

.announcement-card--video-of-week {
  background: linear-gradient(120deg, #0f2027, #203a43, #2c5364);
}

.opacity-80 {
  opacity: 0.8;
}

.announcement-card__video {
  display: flex;
  justify-content: center;
  width: 100%;
}

.announcement-card__storage-video-frame,
.announcement-card__youtube-frame {
  width: min(100%, 640px);
  overflow: hidden;
  border-radius: 18px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.25);
  background: rgba(255, 255, 255, 0.08);
}

.announcement-card__youtube-frame {
  aspect-ratio: 16 / 9;
}

.announcement-card__youtube-iframe {
  display: block;
  width: 100%;
  height: 100%;
  border: 0;
}

.announcement-card__storage-video-frame {
  --announcement-video-max-height: clamp(220px, 52vh, 460px);
  display: flex;
  justify-content: center;
  align-items: center;
  max-height: var(--announcement-video-max-height);
  background: #000;
}

.announcement-card__storage-video {
  display: block;
  width: auto;
  height: auto;
  max-width: 100%;
  max-height: var(--announcement-video-max-height);
  object-fit: contain;
  background: #000;
}

@media (max-width: 960px) {
  .announcement-card__storage-video-frame {
    --announcement-video-max-height: clamp(200px, 50vh, 380px);
  }
}

@media (max-width: 600px) {
  .announcement-card {
    padding: 20px !important;
    border-radius: 16px !important;
  }

  .announcement-card__storage-video-frame,
  .announcement-card__youtube-frame {
    border-radius: 12px;
  }

  .announcement-card__storage-video-frame {
    --announcement-video-max-height: clamp(180px, 45vh, 300px);
  }
}
</style>
